import { NavLink, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Home, CalendarDays, Users, Map, FileText, Settings, Shield, ChevronLeft, ChevronRight,
  Activity, LogOut, Building2, ClipboardList, ShieldCheck, BarChart3, Lock, Wallet, BadgeCheck,
} from 'lucide-react';
import { useAuth } from '../stores/auth';
import { useVomPermissions } from '../hooks/useVomPermissions';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { show_phi, phiText } from '../lib/phiConfig';
import { authedFetch } from '../lib/supabase';
import { applyLayoutToItems } from '../lib/sidebarRegistry';

// Every item now carries a stable `key` that matches the backend layout
// registry (see `SIDEBAR_ITEM_KEYS` in server.py and `sidebarRegistry.js`).
// Items with `locked: true` are never affected by the super-admin layout
// customization so admins can't hide themselves out of Settings/Home.
//
// `flag` (optional) is the org feature-flag key that governs this item's
// visibility.  If the org has the flag turned off the nav item is hidden
// regardless of role and regardless of the saved layout.
const navByRole = {
  org_admin: [
    { key: 'dashboard',     to: '/dashboard',    icon: Home,        label: 'Dashboard',      locked: true },
    { key: 'schedule',      to: '/schedule',     icon: CalendarDays, label: phiText('Schedule', 'Task Calendar'), flag: 'schedule' },
    { key: 'patients',      to: '/patients',     icon: Users,       label: 'Patients',       flag: 'patients' },
    { key: 'map',           to: '/map',          icon: Map,         label: 'Map',            flag: 'schedule' },
    { key: 'documents',     to: '/documents',    icon: FileText,    label: 'Documents',      flag: 'documents' },
    { key: 'team',          to: '/team',         icon: Building2,   label: 'Team',           flag: 'team_management' },
    { key: 'credentialing', to: '/credentialing', icon: ShieldCheck, label: 'Credentialing', flag: 'compliance' },
    { key: 'kpi',           to: '/kpi',          icon: BarChart3,   label: 'KPI & Revenue',  flag: 'kpi_analytics' },
    { key: 'invoices',      to: '/invoices',     icon: FileText,    label: 'Invoices & A/R', flag: 'billing' },
    { key: 'payroll',       to: '/payroll',      icon: Wallet,      label: 'Payroll',        flag: 'payroll' },
    { key: 'compliance',    to: '/compliance',   icon: Shield,      label: 'Compliance',     flag: 'compliance' },
    { key: 'security',      to: '/security',     icon: Lock,        label: 'Security' },
    { key: 'audit_logs',    to: '/audit-logs',   icon: Activity,    label: 'Audit Logs' },
    { key: 'settings',      to: '/settings',     icon: Settings,    label: 'Settings',       locked: true },
  ],
  therapist: [
    { key: 'schedule',   to: '/my-schedule', icon: CalendarDays, label: phiText('My Schedule', 'My Task Calendar'), locked: true },
    { key: 'patients',   to: '/patients',    icon: Users,       label: 'My Patients',   flag: 'patients' },
    { key: 'map',        to: '/map',         icon: Map,         label: 'Map',           flag: 'schedule' },
    { key: 'my_profile', to: '/my-profile',  icon: ClipboardList, label: 'My Profile' },
    { key: 'documents',  to: '/documents',   icon: FileText,    label: 'Documents',     flag: 'documents' },
    { key: 'payroll',    to: '/payroll',     icon: Wallet,      label: 'My Payroll',    flag: 'payroll' },
    { key: 'settings',   to: '/settings',    icon: Settings,    label: 'Settings',      locked: true },
  ],
  virtual_ops_manager: [
    { key: 'vom_home',      to: '/vom',           icon: Home,        label: 'Operations',    locked: true },
    { key: 'schedule',      to: '/schedule',      icon: CalendarDays, label: 'Schedule',      feature: 'schedule',       flag: 'schedule' },
    { key: 'patients',      to: '/patients',      icon: Users,       label: 'Patients',      feature: 'patients',       flag: 'patients' },
    { key: 'map',           to: '/map',           icon: Map,         label: 'Map',           feature: 'schedule',       flag: 'schedule' },
    { key: 'team',          to: '/team',          icon: Building2,   label: 'Team',          feature: 'team_mgmt',      flag: 'team_management' },
    { key: 'credentialing', to: '/credentialing', icon: ShieldCheck, label: 'Credentialing', feature: 'team_mgmt',      flag: 'compliance' },
    { key: 'documents',     to: '/documents',     icon: FileText,    label: 'Documents',     feature: 'documents',      flag: 'documents' },
    { key: 'kpi',           to: '/kpi',           icon: BarChart3,   label: 'KPI',           feature: 'kpi',            flag: 'kpi_analytics' },
    { key: 'invoices',      to: '/invoices',      icon: FileText,    label: 'Invoices & A/R', feature: 'invoices',      flag: 'billing' },
    { key: 'payroll',       to: '/payroll',       icon: Wallet,      label: 'Payroll',       feature: 'payroll',        flag: 'payroll' },
    { key: 'my_credentials', to: '/team/me',      icon: BadgeCheck,  label: 'My Credentials', locked: true },
    { key: 'settings',      to: '/settings',      icon: Settings,    label: 'Settings',      locked: true },
  ],
  super_admin: [
    { key: 'platform',   to: '/super-admin', icon: Home,     label: 'Platform',   locked: true },
    { key: 'invoices',   to: '/invoices',    icon: FileText, label: 'Client Invoices' },
    { key: 'payroll',    to: '/payroll',     icon: Wallet,   label: 'Payroll' },
    { key: 'compliance', to: '/compliance',  icon: Shield,   label: 'Compliance' },
    { key: 'security',   to: '/security',    icon: Lock,     label: 'Security' },
    { key: 'audit_logs', to: '/audit-logs',  icon: Activity, label: 'Audit Logs' },
    { key: 'settings',   to: '/settings',    icon: Settings, label: 'Settings',   locked: true },
  ],
};

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { profile, org, signOut, viewingOrgId, clearViewingOrg } = useAuth();
  const nav = useNavigate();
  const allItems = navByRole[profile?.role] || navByRole.therapist;
  const vomPerms = useVomPermissions();
  const { flags } = useFeatureFlags();

  // Per-org sidebar layout customization.  Super admin uses their own
  // platform-level sidebar (not customized per client), so we skip the
  // fetch for that role; every other role reads their current org's layout
  // (which is fully org-scoped and RBAC-checked on the backend).
  const isSuperOnPlatform = profile?.role === 'super_admin' && !viewingOrgId;
  const layoutQ = useQuery({
    queryKey: ['sidebar-layout', org?.id],
    queryFn: () => authedFetch(`/sidebar-layout/${org.id}`),
    enabled: !!org?.id && !isSuperOnPlatform,
    staleTime: 60 * 1000,
    // Silent failure — if the layout endpoint 500s we fall back to defaults
    // so the app never becomes unnavigable.
    retry: 1,
  });

  // 1) Apply role-based feature gating for VOMs (existing behaviour).
  const roleFiltered = allItems.filter((it) => {
    if (!it.feature) return true;
    return vomPerms.hasAny(it.feature);
  });
  // 2) Apply per-org feature flags (super_admin platform view bypasses).
  //    The Patients link is additionally gated by the master PHI toggle and
  //    is fail-closed: hidden unless `show_phi` is explicitly true.
  const flagFiltered = isSuperOnPlatform
    ? roleFiltered
    : roleFiltered.filter((it) =>
        (!it.flag || !flags || flags[it.flag] !== false) &&
        (it.key !== 'patients' || (show_phi && flags?.show_phi === true)) &&
        // Map (therapist service-area tracking) is home-health only.
        (it.key !== 'map' || org?.type === 'home_health') &&
        // Therapist / VOM: employee-management only — no KPI / revenue unless PHI on
        (!['therapist', 'virtual_ops_manager'].includes(profile?.role) || !['kpi', 'invoices'].includes(it.key) || show_phi) &&
        // Invoicing is Operon ↔ client orgs / org ↔ vendors only — never VOM or therapists
        (it.key !== 'invoices' || !['therapist', 'virtual_ops_manager'].includes(profile?.role))
      );
  // 3) Apply the org's saved layout on top (hide + reorder).
  const items = applyLayoutToItems(flagFiltered, layoutQ.data?.items)
    // VOM "My Credentials" opens the VOM's own folder (/team/:id)
    .map((it) => (it.key === 'my_credentials' && profile?.id ? { ...it, to: `/team/${profile.id}` } : it));

  const onSignOut = async () => {
    await signOut();
    nav('/login');
  };

  return (
    <aside
      data-testid="desktop-sidebar"
      className={`desktop-only flex flex-col bg-white border-r border-[var(--color-border)] transition-[width] duration-200 ${
        collapsed ? 'w-[72px]' : 'w-[260px]'
      }`}
    >
      {/* Logo */}
      <div className={`px-4 py-4 border-b border-[var(--color-border)] ${collapsed ? 'flex justify-center' : 'flex items-center gap-3'}`}>
        <img
          src="/om-icon.png"
          alt="Operon Management"
          className={`object-contain shrink-0 ${collapsed ? 'h-10 w-10' : 'h-11 w-11'}`}
        />
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="font-display text-base font-semibold text-[var(--color-ink)] leading-tight">Operon</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-[var(--color-muted)] font-semibold leading-tight mt-1">Practice Management</div>
          </div>
        )}
      </div>

      {/* Org/User */}
      {!collapsed && (
        <div className="px-4 py-3 border-b border-[var(--color-border)]">
          <div className="text-xs text-[var(--color-muted)] uppercase tracking-wider mb-1">
            {viewingOrgId && profile?.role === 'virtual_ops_manager' ? 'Viewing client' : 'Organization'}
          </div>
          <div className="text-sm font-semibold text-[var(--color-ink)] truncate" data-testid="sidebar-active-org">
            {org?.name || (profile?.role === 'super_admin' ? 'Operon Platform' : profile?.role === 'virtual_ops_manager' ? 'Operon \u2014 VOM' : '\u2014')}
          </div>
          <div className="text-xs text-[var(--color-muted)] mt-0.5 capitalize">{profile?.role?.replace(/_/g, ' ')}</div>
          {/* Phase 20: VOM client-context switcher hint */}
          {profile?.role === 'virtual_ops_manager' && viewingOrgId && (
            <button
              type="button"
              data-testid="sidebar-exit-client-context"
              onClick={() => clearViewingOrg()}
              className="mt-2 inline-flex items-center gap-1 text-[10.5px] text-[var(--color-teal-deep)] underline hover:no-underline"
              title="Return to your VOM portal"
            >
              {'\u2190'} Exit client
            </button>
          )}
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3">
        {items.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 mx-2 my-0.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-[rgba(59,158,223,0.12)] text-[#0B6FAD] border border-[rgba(59,158,223,0.30)]'
                  : 'text-[var(--color-slate)] hover:bg-[rgba(27,58,107,0.05)] hover:text-[var(--color-ink)] border border-transparent'
              }`
            }
            title={collapsed ? item.label : undefined}
          >
            <item.icon size={18} strokeWidth={2} className="shrink-0" />
            {!collapsed && <span className="truncate">{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Sign out */}
      <button
        data-testid="sidebar-sign-out"
        onClick={onSignOut}
        className="flex items-center gap-3 px-4 py-2.5 mx-2 mb-2 rounded-lg text-sm font-medium text-[var(--color-slate)] hover:bg-[rgba(232,115,90,0.10)] hover:text-[#8A3B2B] border border-transparent transition-colors"
        title="Sign out"
      >
        <LogOut size={18} />
        {!collapsed && <span>Sign out</span>}
      </button>

      {/* Collapse toggle */}
      <button
        data-testid="sidebar-collapse-toggle"
        onClick={() => setCollapsed(c => !c)}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-expanded={!collapsed}
        className="flex items-center justify-center py-2.5 border-t border-[var(--color-border)] text-[var(--color-muted)] hover:text-[var(--color-ink)] hover:bg-[var(--color-surface-2)] transition-colors min-h-[44px]"
      >
        {collapsed ? <ChevronRight size={16} aria-hidden="true" /> : <ChevronLeft size={16} aria-hidden="true" />}
      </button>
    </aside>
  );
}
